"use client";
import React from "react";
import { calcOrderTotals, parseNum } from "../pages/panel/orders/orderCalculations";
import type { ProductItem } from "../pages/panel/types/order";

type Props = {
  price?: string;
  gst?: string;
  discount?: string;
  fixing?: string;
  transport?: string;
  total?: string;
  advance?: string;
  advance2?: string;
  balance?: string;
  products?: ProductItem[];
};

const money = (value: string | number | null | undefined, fallback: number) =>
  parseNum(value, fallback).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const PriceSummaryReadOnly: React.FC<Props> = ({
  price,
  gst,
  discount,
  fixing,
  transport,
  total,
  advance,
  advance2,
  balance,
  products = [],
}) => {
  const totals = calcOrderTotals({
    products,
    discount,
    fixing,
    transport,
    advance,
    advance2,
  });

  const subtotal = parseNum(price, totals.subtotal);
  const gstVal = parseNum(gst, totals.gst);
  const discountVal = parseNum(discount, totals.discount);
  const fixingVal = parseNum(fixing, totals.fixing);
  const transportVal = parseNum(transport, totals.transport);
  const grandTotal = parseNum(total, totals.grandTotal);
  const advanceVal = parseNum(advance, 0);
  const advance2Val = parseNum(advance2, 0);
  const balanceVal = Math.max(0, parseNum(balance, totals.balance));

  const cell = (label: string, value: string, valueClass = "text-gray-900") => (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5 min-w-0">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm font-bold truncate ${valueClass}`}>₹{value}</p>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-orange-50 via-white to-amber-50 border-2 border-orange-200 rounded-xl p-4 relative z-0 isolate">
      <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">💰 Price Summary</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
        {cell("Total", money(subtotal, subtotal), "text-orange-700")}
        {cell("GST", money(gstVal, gstVal), "text-blue-700")}
        {cell("Discount", money(discountVal, discountVal), "text-red-700")}
        {cell("Fixing", money(fixingVal, fixingVal), "text-purple-700")}
        {cell("Transport", money(transportVal, transportVal), "text-green-700")}
        {cell("Advance 1", money(advanceVal, advanceVal), "text-teal-700")}
        {cell("Advance 2", money(advance2Val, advance2Val), "text-teal-700")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg p-3 text-white">
          <p className="text-[11px] font-bold text-cyan-100 uppercase">Grand Total</p>
          <p className="text-lg font-bold">₹{money(grandTotal, grandTotal)}</p>
        </div>
        <div className="bg-white border-2 border-slate-300 rounded-lg p-3">
          <p className="text-[11px] font-bold text-gray-500 uppercase">Balance</p>
          <p className="text-lg font-bold text-slate-800">₹{money(balanceVal, balanceVal)}</p>
        </div>
      </div>
    </div>
  );
};
